import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import useAnimationFrame from '../hooks/useAnimationFrame';
import { MathUtils } from 'three';

import styles from './styles.module.scss';
import hoverIcon from '../assets/images/icons/masked-image-hover-icon.svg';
import useIsMobile from '../hooks/useIsMobile';

type Props = {
    radius: number,
    smoothing: number,
    feather: number,
    parallax: number,
    baseSrc: string,
    overlaySrc: string,
    style?: CSSProperties,
    className?: string
}

type Point = { x: number, y: number };

export default function MaskedImage({
  radius,
  baseSrc,
  smoothing,
  feather,
  parallax,
  overlaySrc,
  style,
  className
}: Props): JSX.Element
{
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLImageElement>(null);
  const mouseTargetRef = useRef<Point>({ x: 0, y: 0 });
  const mouseRef = useRef<Point | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const gradientRef = useRef<HTMLCanvasElement | null>(null);

  const isMobile = useIsMobile();

  const onFrame = useCallback(() =>
  {
    const canvas = canvasRef.current;
    const underlay = imageRef.current;
    const overlay = overlayRef.current;

    if (!canvas || !underlay?.complete || !overlay?.complete) return;

    if (!ctxRef.current) {
      ctxRef.current = canvas.getContext('2d');
    }

    const ctx = ctxRef.current;
    if (!ctx) return;

    if (!gradientRef.current) {
      const tmpCanvas = document.createElement('canvas');
      const s = radius * 2;
      tmpCanvas.width = s;
      tmpCanvas.height = s;
      const tmpCtx = tmpCanvas.getContext('2d');
      if (!tmpCtx) return;
      const grad = tmpCtx.createRadialGradient(radius, radius, 0, radius, radius, radius);
      grad.addColorStop(1.0 - feather, 'rgba(0, 0, 0, 1)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

      // Fill with gradient
      tmpCtx.fillStyle = grad;
      tmpCtx.fillRect(0, 0, s, s);
      gradientRef.current = tmpCanvas;
    }

    const gradient = gradientRef.current;
    if (!gradient) return;

    const pos = mouseRef.current;
    const w = underlay.naturalWidth;
    const h = underlay.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    ctx.clearRect(0, 0, w, h);

    if (pos) {
      const parX = -(pos.x / w * 2.0 - 1.0) * parallax;
      const parY = -(pos.y / h * 2.0 - 1.0) * parallax;


      ctx.drawImage(gradient, pos.x - radius, pos.y - radius);
      ctx.globalCompositeOperation = 'source-atop';
      ctx.fillStyle = 'rgba(255, 255, 255, 1)';
      ctx.fillRect(pos.x - radius, pos.y - radius, 2 * radius, 2 * radius);
      ctx.drawImage(overlay, parX, parY);
      ctx.globalCompositeOperation = 'destination-over';
    }
    ctx.drawImage(underlay, 0, 0);

    if (pos) {
      pos.x = MathUtils.lerp(mouseTargetRef.current.x, pos.x, smoothing);
      pos.y = MathUtils.lerp(mouseTargetRef.current.y, pos.y, smoothing);
    }
  }, [ feather, radius, smoothing, parallax ]);

  useAnimationFrame(onFrame);
  const [hideHoverIcon, setHideHoverIcon] = useState(false);

  const onMouseMove = useCallback((e: MouseEvent) =>
  {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    mouseTargetRef.current.x = (e.clientX - rect.left) / rect.width * canvas.width;
    mouseTargetRef.current.y = (e.clientY - rect.top) / rect.height * canvas.height;

    if (!mouseRef.current) {
      mouseRef.current = { ...mouseTargetRef.current };
    }
  }, []);

  useEffect(() =>
  {
    document.addEventListener('mousemove', onMouseMove);
    return () =>
    {
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, [ onMouseMove ]);

  return (<>
    <canvas ref={canvasRef} onMouseMove={() => setHideHoverIcon(true)} className={className} style={{ position: 'relative', ...style }}>
      <img src={baseSrc} className='hiddenMaskedImage' ref={imageRef}/>
      <img src={overlaySrc} className='hiddenMaskedImage' ref={overlayRef}/>
    </canvas>
    {!isMobile && <img src={hoverIcon} alt='hover icon'
      className={hideHoverIcon ? styles.hideMaskedImageIcon : ''}
      style={{
        width: '4rem',
        position: 'relative',
        top: '-8.4rem',
        left: '53%'
      }} />}
  </>);
}